import { Button, Col, Form, Row, Select, Wizard } from '@valid-eval/shared-react-components';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CheckboxList from 'components/Form/CheckboxList';
import { getSupportEmail } from 'config';
import {
  useAllMatchingCategories,
  useInheritedMatchingCategories,
  useMatchingCategories,
  useSaveMatchingCategories,
} from 'data/features/eventsHooks';
import { MatchingCategory, ParentTagName } from 'data/features/eventsTypes';
import ContactSupportModal from 'screens/app/components/ContactSupportModal';
import { useDebouncedCallback } from 'utils/debounce-promise';
import useBooleanFlag from 'utils/hooks/useBooleanFlag';
import { useShowErrorIfRejected } from 'utils/hooks/useShowErrorIfRejected';

import Styles from 'components/Form/Select.module.scss';
import { EventStepComponentProps } from '.';
import EditEventStepForm from './components/EditEventStepForm';
import SubmitForReviewButton from './components/SubmitForReviewButton';

type CategoriesFormData = {
  inheritedTechTypes: (string | false)[];
  techTypes: (string | false)[];
};

const matchingCategoryToOption = (category: MatchingCategory) => ({
  value: category.id,
  label: category.name,
});

function CategoriesStepComponent({ event, label, subLabel }: EventStepComponentProps) {
  const { t } = useTranslation();
  const [showContactSupport, openContactSupport, closeContactSupport] = useBooleanFlag();
  const supportEmail = getSupportEmail();
  const { saveMatchingCategories, saving } = useSaveMatchingCategories();
  const showErrorIfRejected = useShowErrorIfRejected();

  const techTypeParams = { eventId: event.id, parentCategoryName: ParentTagName.TECHNOLOGY_TYPE };
  const stageOfParams = { eventId: event.id, parentCategoryName: ParentTagName.STAGE_OF_VENTURE };
  const inheritedTechTypes = useInheritedMatchingCategories(techTypeParams);
  const allTechTypes = useAllMatchingCategories(techTypeParams);
  const techTypes = useMatchingCategories(techTypeParams);
  const stageOfVenture = useMatchingCategories(stageOfParams);
  const inheritedStageOfVenture = useInheritedMatchingCategories(stageOfParams);

  const methods = useForm<CategoriesFormData>({
    mode: 'onBlur',
    defaultValues: {
      inheritedTechTypes: inheritedTechTypes.map((category) =>
        category.enabled ? category.id : false,
      ),
      techTypes: allTechTypes.map(
        (category) => techTypes.find((t) => t.id === category.id)?.id || false,
      ),
    },
  });

  const techTypesSelected = methods.watch('techTypes');

  // Options for inherited tech types
  const inheritedTechTypeOptions = useMemo(
    () => inheritedTechTypes.map(matchingCategoryToOption),
    [inheritedTechTypes],
  );

  // Options for all tech types select it filters inherited tech types
  const allTechTypeOptions = useMemo(
    () => allTechTypes.map(matchingCategoryToOption),
    [allTechTypes],
  );

  // Options for selectable tech types it filters inherited tech types and already selected tech types
  const selectableTechTypes = useMemo(() => {
    const categoriesToExclude = new Set(techTypesSelected.filter(Boolean) as string[]);
    return allTechTypes.filter((category) => !categoriesToExclude.has(category.id));
  }, [allTechTypes, techTypesSelected.filter(Boolean).join(',')]);

  // On select tech type update the form value
  const handleSelectTechType = (value: any) => {
    if (!value) return;

    const { techTypes } = methods.getValues();
    const index = allTechTypeOptions.findIndex((category) => category.value === value.id);

    if (index !== -1) {
      techTypes[index] = value.id;
      methods.setValue('techTypes', [...techTypes]);
      methods.trigger('techTypes');
    }
  };

  // Save function
  const save = async (data: CategoriesFormData) => {
    const allTags = new Set(
      [
        ...data.inheritedTechTypes,
        ...data.techTypes,
        ...stageOfVenture.map((category) => category.id),
        ...inheritedStageOfVenture.map((category) => category.id),
      ].filter(Boolean),
    );

    showErrorIfRejected(await saveMatchingCategories(event.id, Array.from(allTags) as string[]));
  };

  // Debounced save callback
  const debouncedSave = useDebouncedCallback(
    async (data: CategoriesFormData) => {
      const errorKeys = Object.keys(methods.formState.errors);
      for (const errorKey of errorKeys) {
        if (errorKey === 'inheritedTechTypes' || errorKey === 'techTypes') {
          data[errorKey as keyof CategoriesFormData] = [] as any;
        }
      }
      save(data);
    },
    [methods.formState.errors],
    2000,
  );

  // Submit handler
  const handleSubmit = async (data: CategoriesFormData) => {
    await save(data);
  };

  // Submit form when values change
  useEffect(() => {
    const subscription = methods.watch((_, { type }) => {
      if (type !== 'change') return;
      methods.trigger();
      debouncedSave(methods.getValues());
    });
    return subscription.unsubscribe;
  }, [methods, debouncedSave]);

  const handleContactSupport = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    openContactSupport();
  };

  return (
    <EditEventStepForm methods={methods} onSubmit={handleSubmit} label={label} subLabel={subLabel}>
      <Row className="mt-3 mb-2">
        <Col xs={12}>
          <CheckboxList
            id="inherited-tech-types"
            name="inheritedTechTypes"
            label={t('event.form.technology_type_inherited_categories')}
            options={inheritedTechTypeOptions}
            toggleButton
            helpText={t('event.form.technology_type_inherited_categories_help')}
          />
        </Col>
      </Row>
      <Row className="mt-3 mb-2">
        <Col xs={12}>
          <Form.Group className="mb-2">
            <Form.Control
              as={Select}
              isForm
              className={Styles.FormControl}
              placeholder={t('event.form.search_category_placeholder')}
              options={selectableTechTypes}
              isSearchable={true}
              isClearable={true}
              value={''}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              onChange={handleSelectTechType}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mt-3 mb-2">
        <Col xs={12}>
          <CheckboxList
            id="tech-types"
            name="techTypes"
            options={allTechTypeOptions}
            toggleButton
            hideOnUnselected={true}
          />
        </Col>
      </Row>
      <Row className="mt-3 mb-2">
        <Col xs={12}>
          <p className="d-print-none text-pre-wrap text-sm text-muted">
            <Form.Text muted>
              {t('event.form.search_category_help', { email: supportEmail })}
              &nbsp;
              <a href={`mailto:${supportEmail}`} onClick={handleContactSupport}>
                {t('event.form.search_category_help_link')}
              </a>
            </Form.Text>
          </p>
        </Col>
      </Row>
      <Wizard.Footer>
        <div className="d-flex justify-content-end align-items-center">
          <Button variant="secondary" type="submit" className="me-3" disabled={saving}>
            {t('event.form.save_progress')}
          </Button>
          <SubmitForReviewButton event={event} beforeSubmit={methods.handleSubmit(handleSubmit)} />
        </div>
      </Wizard.Footer>
      {showContactSupport && (
        // @ts-ignore
        <ContactSupportModal onCancel={closeContactSupport} />
      )}
    </EditEventStepForm>
  );
}

export default CategoriesStepComponent;
